import { useEffect, useMemo, useState } from 'react';
import { useOpsRecordList } from '../hooks/useDailyInstallations';
import { BRAND, Panel, num, selectClass } from './OpsPageKit';

// A filterable list of the individual records behind an ops report page: any
// date range, Region B / Region N, one name, and free-text search. The page
// supplies the table columns; everything else - filters, counts, the "showing
// the latest 500" note - lives here so the pages stay consistent.
//
//   <OpsRecordList page="internal-remediations" title="Remediations"
//     nameLabel="Engineer" searchPlaceholder="…" columns={[{ head, cell }]} />

export const ukDate = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${today().slice(0, 7)}-01`;

// The Daily / Range date control, the same one Daily Installations uses: Daily
// is one date, Range is from-to. Returns the {from, to} to query (a day is
// from = to = that day), whether the range is back to front, and the control.
export function useDateModeRange(defaultMode = 'day') {
  const [mode, setMode] = useState(defaultMode);
  const [date, setDate] = useState(today());
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const range = mode === 'day' ? { from: date, to: date } : { from, to };
  const bad = mode === 'range' && Boolean(from && to && from > to);
  const control = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex overflow-hidden rounded-md border border-gray-300">
        {[['day', 'Daily'], ['range', 'Range']].map(([m, label]) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-3 py-1.5 text-sm font-medium ${mode === m ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            style={mode === m ? { background: BRAND } : undefined}
          >
            {label}
          </button>
        ))}
      </div>
      {mode === 'day' ? (
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={selectClass} aria-label="Date" />
      ) : (
        <>
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={selectClass} aria-label="From" />
          <span className="text-xs text-gray-400">to</span>
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={selectClass} aria-label="To" />
        </>
      )}
    </div>
  );
  return { ...range, bad, control };
}

function OpsRecordList({
  page, title, nameLabel = 'Engineer', searchPlaceholder = 'Search…', columns, rowKey = (r) => r.id,
}) {
  const { from, to, bad: badRange, control: dateControl } = useDateModeRange();
  const [contract, setContract] = useState('');
  const [engineer, setEngineer] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filters = useMemo(() => ({
    from, to, contract, engineer, q,
  }), [from, to, contract, engineer, q]);
  const { data, isLoading, isError, isFetching } = useOpsRecordList(page, badRange ? null : filters);

  const rows = data?.rows || [];
  const names = data?.engineers || [];
  // Keep the chosen name selectable even if the new range has none for them.
  const options = engineer && !names.some((e) => e.name === engineer) ? [{ name: engineer, n: 0 }, ...names] : names;

  return (
    <div className="mt-6">
      <Panel title={title}>
        <div className="flex flex-wrap items-end gap-2 border-b border-gray-100 px-4 pb-3 pt-1">
          {dateControl}
          <label className="text-xs text-gray-500">
            Region
            <select value={contract} onChange={(e) => setContract(e.target.value)} className={`mt-0.5 block ${selectClass}`}>
              <option value="">Both regions</option>
              <option value="BRSK">Region B</option>
              <option value="NETOMNIA">Region N</option>
            </select>
          </label>
          <label className="text-xs text-gray-500">
            {nameLabel}
            <select value={engineer} onChange={(e) => setEngineer(e.target.value)} className={`mt-0.5 block max-w-[14rem] ${selectClass}`}>
              <option value="">Everyone</option>
              {options.map((e) => <option key={e.name} value={e.name}>{`${e.name} (${num(e.n)})`}</option>)}
            </select>
          </label>
          <label className="min-w-[12rem] flex-1 text-xs text-gray-500">
            Search
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={searchPlaceholder} className={`mt-0.5 block w-full ${selectClass}`} />
          </label>
          {(engineer || search || contract) ? (
            <button type="button" onClick={() => { setEngineer(''); setSearch(''); setContract(''); }} className="pb-1.5 text-xs font-medium text-brand hover:underline">
              Clear
            </button>
          ) : null}
        </div>

        <div className="px-4 py-2 text-xs text-gray-500">
          {badRange && <span className="text-rose-600">The From date is after the To date.</span>}
          {!badRange && isLoading && 'Loading…'}
          {!badRange && isError && <span className="text-rose-600">Could not load the list.</span>}
          {!badRange && data && (
            <>
              <strong className="text-gray-800">{num(data.total)}</strong>
              {` from ${ukDate(data.from)} to ${ukDate(data.to)}`}
              {data.total > rows.length ? ` — showing the latest ${num(rows.length)}; narrow the dates or search to see the rest` : ''}
              {isFetching ? ' · updating…' : ''}
            </>
          )}
        </div>

        {!badRange && rows.length ? (
          <div className="max-h-[36rem] overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>{columns.map((c) => <th key={c.head} className="px-3 py-2">{c.head}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={rowKey(r)} className="align-top hover:bg-gray-50/60">
                    {columns.map((c) => <td key={c.head} className={`px-3 py-2 ${c.className || 'text-gray-700'}`}>{c.cell(r)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (!badRange && data ? <p className="px-4 py-8 text-center text-sm text-gray-400">Nothing matches.</p> : null)}
      </Panel>
    </div>
  );
}

export default OpsRecordList;
